import { API } from 'constants/api'
import { TrackingProps } from 'constants/map'
import { PAGE_PATH } from 'constants/pagePath'
import {
  ConnectionIdContentProps,
  DOMAIN,
  DurationContentProps,
  LocationContentProps,
  MessageProps,
  PartnerNameContentProps,
  StatusContentProps,
  WebSocketProps,
} from 'constants/websocket'
import {
  HistoryStackContext,
  HistoryStackContextProps,
} from 'contexts/HistoryStackContext'
import { JwtContext } from 'contexts/JwtContext'
import {
  WebSocketUtilsContext,
  WebSocketUtilsContextProps,
} from 'contexts/WebSocketUtilsContext'
import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { QnStatesProps } from 'states/QnState'
import { useSender } from 'utils/axios'
import { transitionWithAction } from 'utils/commonUtils'

/**
 * Websocketを開始するHooksを起動
 * @param websocket 起動するWebsocket
 * @param qnStates QnState更新用のhooks型State
 */
export const useWebsocket = (
  websocket: WebSocketProps,
  qnStates: QnStatesProps
): void => {
  const webSocketUtilsContext = useContext(WebSocketUtilsContext)
  const [webSocketUtils, setWebSocketUtils] = webSocketUtilsContext
  const [connectionId, setConnectionId] = useState<string>('')
  const [jwt] = useContext(JwtContext)
  const [, getQnStatus] = useSender(API.QN_STATUS)

  const history = useHistory()

  const historyStackContext = useContext(HistoryStackContext)

  const startWebSocket = (): void => {
    if (!jwt.length) {
      return
    }
    console.log('WebSocket Start', Date())
    const sock = new WebSocket(
      `${DOMAIN}${websocket.url}?jwt=${jwt}${
        connectionId.length ? `&connectionId=${connectionId}` : ''
      }`
    )
    sock.onopen = async (): Promise<void> => {
      console.log('WebSocket Connected', Date())
      // WebSocketを外部から終了できるよう、sockを確保する
      // （sock.closeのみを抜き出すと正常に終了できなかったため、sockごと確保）
      if (!webSocketUtils.manualExit) {
        // 自動再接続前、WebSocketがつながっていないタイミングがあるためQNステータスを受信する
        const result = await getQnStatus({})
        if (result && result.data && result.data.data) {
          const { qnStatus, locationSharingTimeout } = result.data.data
          qnStates.setState({
            ...qnStates.state,
            qnStatus: qnStatus || 'FILLING',
            locationSharingTimeout,
          })
        }
      }
      setWebSocketUtils({
        ...webSocketUtils,
        sock: sock,
        manualExit: false,
        connected: true,
      })
    }

    sock.onclose = (event): void => {
      onClose(event, startWebSocket, webSocketUtilsContext)
    }

    sock.onerror = (): void => {
      onError(history, historyStackContext, webSocketUtilsContext)
    }

    // サーバーから受け取ったデータの処理
    sock.onmessage = (event): void => {
      console.log('WebSocket Message', Date())
      onMessage(event, qnStates, setConnectionId, webSocketUtilsContext)
    }
  }

  // WebSocketを使用可能にする
  useEffect(() => {
    if (jwt) {
      setWebSocketUtils({
        ...webSocketUtils,
        startFunc: startWebSocket,
      })
    }
  }, [jwt])

  // WebSocketの監視
  useEffect(() => {
    // WebSocketの起動の監視
    if (
      webSocketUtils.start &&
      webSocketUtils.startFunc &&
      !webSocketUtils.connected
    ) {
      webSocketUtils.startFunc()
      setWebSocketUtils({
        ...webSocketUtils,
        start: false,
      })
    }

    // WebSocketの終了の監視
    if (
      webSocketUtils.exit &&
      webSocketUtils.sock &&
      webSocketUtils.connected
    ) {
      webSocketUtils.sock.close()
      setWebSocketUtils({
        ...webSocketUtils,
        exit: false,
        manualExit: true,
      })
    }

    // 最新の情報をイベントリスナー先に伝える
    if (webSocketUtils.sock) {
      webSocketUtils.sock.onmessage = (event): void => {
        onMessage(event, qnStates, setConnectionId, webSocketUtilsContext)
      }
      webSocketUtils.sock.onerror = (): void => {
        onError(history, historyStackContext, webSocketUtilsContext)
      }
      webSocketUtils.sock.onclose = (event): void => {
        onClose(event, startWebSocket, webSocketUtilsContext)
      }
    }
  }, [
    webSocketUtils.start,
    webSocketUtils.exit,
    webSocketUtils.startFunc,
    qnStates,
  ])
}

/**
 * WebSocket受信時の処理
 * @param event メッセージ受信イベント
 * @param qnStates QNState更新用のhooks型State
 */
const onMessage = (
  event: any,
  qnStates: QnStatesProps,
  setConnectionId: React.Dispatch<React.SetStateAction<string>>,
  webSocketUtilsContext: WebSocketUtilsContextProps
): void => {
  const data: MessageProps = JSON.parse(event.data)
  console.log('Catch WebSocket Data', data, Date())
  switch (data.type) {
    case 'location':
      processLocation(data.content, qnStates)
      break
    case 'status':
      processStatus(data.content, qnStates)
      break
    case 'partnerName':
      processPartnerName(data.content, qnStates)
      break
    case 'duration':
      processDuration(data.content, qnStates)
      break
    case 'connectionId':
      processConnectionId(data.content, setConnectionId)
      break
    case 'deleteConnection':
      processDeleteConnection(webSocketUtilsContext)
      break
  }
}

/**
 * WebSocket終了時の処理
 * @param event イベント
 * @param startFunc WebSocket接続用関数
 * @param webSocketUtilsContext WebSocket用Context
 */
const onClose = (
  event: any,
  startFunc: () => void,
  webSocketUtilsContext: WebSocketUtilsContextProps
): void => {
  console.log('WebSocket Close', Date())
  const [webSocketUtils, setWebSocketUtils] = webSocketUtilsContext
  if (!webSocketUtils.manualExit) {
    // 自動切断(アイドル10分 or 2時間)時は5秒ごとに再接続を試みる
    console.log('WebSocket Reconnecting...', Date())
    setTimeout(startFunc, 5000)
  }
  // 起動できるようにする
  setWebSocketUtils({
    ...webSocketUtils,
    startFunc,
    connected: false,
  })
}

/**
 * WebSocket終了時の処理
 * @param event イベント
 * @param startFunc WebSocket接続用関数
 * @param webSocketUtilsContext WebSocket用Context
 */
const onError = (
  history: any,
  historyStackContext: HistoryStackContextProps,
  webSocketUtilsContext: WebSocketUtilsContextProps
): void => {
  console.log('WebSocket Error', Date())
  const [webSocketUtils] = webSocketUtilsContext
  if (webSocketUtils.manualExit) {
    transitionWithAction(historyStackContext, 'WebSocketエラー', () => {
      history.push(PAGE_PATH.ERROR)
    })
  }
}

/**
 * WebSocket経由で受信した位置情報をStateに格納する
 * @param locations WebSocket経由で受信した位置情報
 * @param qnStates QnState更新用のhooks型State
 */
const processLocation = (
  locationContent: LocationContentProps,
  qnStates: QnStatesProps
): void => {
  const updatedState = qnStates.state

  const partnerStableQueue = [locationContent.validity]
    .concat(qnStates.state.partnerStableQueue)
    .filter((location, index) => index < 5)
  updatedState.partnerStableQueue = partnerStableQueue

  // 直近5つ全て非妥当の場合、不安定とする
  // 例：4/4が非妥当の場合、安定
  // 例：4/5が非妥当の場合、安定
  // 例：5/5が非妥当の場合、不安定
  const partnerStable =
    partnerStableQueue.filter((validity) => !validity).length !== 5
  updatedState.partnerStable = partnerStable

  // 妥当なときのみマーカーを更新
  if (locationContent.validity) {
    const partner: TrackingProps = {
      lat: locationContent.latitude,
      lng: locationContent.longitude,
      visible: locationContent.locationCooperation === 1,
    }
    updatedState.partner = partner
    updatedState.lastLocationSendDate = locationContent.sendDate
  }

  qnStates.setState(updatedState)
}

/**
 * WebSocket経由で受信したQNステータスをStateに格納する
 * @param locations WebSocket経由で受信したQNステータス
 * @param qnStates QnState更新用のhooks型State
 */
const processStatus = (
  statusContent: StatusContentProps,
  qnStates: QnStatesProps
): void => {
  qnStates.setState({
    ...qnStates.state,
    qnStatus: statusContent.qnStatus,
  })
}

/**
 * WebSocket経由で受信したパートナー名をStateに格納する
 * @param locations WebSocket経由で受信したパートナー名
 * @param qnStates QnState更新用のhooks型State
 */
const processPartnerName = (
  partnerNameContent: PartnerNameContentProps,
  qnStates: QnStatesProps
): void => {
  qnStates.setState({
    ...qnStates.state,
    partnerName: partnerNameContent.partnerName,
  })
}

/**
 * WebSocket経由で受信したパートナー到着までの時間（分）をStateに格納する
 * @param locations WebSocket経由で受信したパートナー到着までの時間（分）
 * @param qnStates QnState更新用のhooks型State
 */
const processDuration = (
  durationContent: DurationContentProps,
  qnStates: QnStatesProps
): void => {
  qnStates.setState({
    ...qnStates.state,
    arrivalDuration: durationContent.duration,
    siteArrivalScheduleDate: durationContent.siteArrivalScheduleDate,
    timeComingSoon: durationContent.timeComingSoon,
  })
}

/**
 * WebSocket経由で受信したWebSocketのConnectionIdをStateに格納する
 * @param locations WebSocket経由で受信したWebSocketのConnectionId
 * @param QNStates QnState更新用のhooks型State
 */
const processConnectionId = (
  connectionIdContent: ConnectionIdContentProps,
  setConnectionId: React.Dispatch<React.SetStateAction<string>>
): void => {
  setConnectionId(connectionIdContent.connectionId)
}

/**
 * WebSocket経由でWebSocketの切断を受信した場合、WebSocketを切断する
 */
const processDeleteConnection = (
  webSocketUtilsContext: WebSocketUtilsContextProps
): void => {
  // WebSocket切断
  exitWebSocket(webSocketUtilsContext, true)
}

/**
 * WebSocketを起動する
 */
export const startWebSocket = (
  webSocketUtilsContext: WebSocketUtilsContextProps
): void => {
  const [webSocketUtils, setWebSocketUtils] = webSocketUtilsContext
  setWebSocketUtils({
    ...webSocketUtils,
    start: true,
  })
}

/**
 * WebSocketを終了する
 */
export const exitWebSocket = (
  webSocketUtilsContext: WebSocketUtilsContextProps,
  forceDisconnect?: boolean
): void => {
  const [webSocketUtils, setWebSocketUtils] = webSocketUtilsContext
  const updatedState = {
    ...webSocketUtils,
    exit: true,
    manualExit: true,
  }
  if (forceDisconnect) {
    // サーバからの切断時
    updatedState.forceDisconnect = forceDisconnect
  }
  setWebSocketUtils(updatedState)
}

/**
 * WebSocketを起動するHooks
 */
export const useStartWebSocket = (condition: boolean): void => {
  const [webSocketUtils, setWebSocketUtils] = useContext(WebSocketUtilsContext)
  const [loaded, setLoaded] = useState<boolean>(false)
  useEffect(() => {
    // 本Hooksが呼ばれたタイミングによってはContext初期化前
    // または、startFuncがundefinedな場合があるため、
    // webSocketUtilsを監視し、WebSocketを起動できるタイミングになったら起動させる
    if (
      !loaded &&
      webSocketUtils.startFunc &&
      !webSocketUtils.connected &&
      condition
    ) {
      startWebSocket([webSocketUtils, setWebSocketUtils])
      setLoaded(true)
    }
  }, [webSocketUtils, condition])
}

/**
 * WebSocketを終了するHooks
 */
export const useExitWebSocket = (condition: boolean): void => {
  const [webSocketUtils, setWebSocketUtils] = useContext(WebSocketUtilsContext)
  const [loaded, setLoaded] = useState<boolean>(false)
  useEffect(() => {
    // 本Hooksが呼ばれたタイミングによってはContext初期化前
    // または、WebSocket未起動or起動中でsockがundefinedな場合があるため、
    // webSocketUtilsを監視し、WebSocketを終了できるタイミングになったら終了させる
    if (
      !loaded &&
      webSocketUtils.sock &&
      webSocketUtils.connected &&
      condition
    ) {
      exitWebSocket([webSocketUtils, setWebSocketUtils], undefined)
      setLoaded(true)
    }
  }, [webSocketUtils, condition])
}
